// src/hooks/usePerformanceMonitor.js — Performance monitoring and Core Web Vitals tracking
import { useEffect, useCallback, useRef } from 'react';

// Performance metrics storage
const metrics = {
  // Core Web Vitals
  FCP: null, // First Contentful Paint
  LCP: null, // Largest Contentful Paint
  FID: null, // First Input Delay
  CLS: null, // Cumulative Layout Shift
  TTFB: null, // Time to First Byte
  
  // Custom metrics
  pageLoadTime: null,
  domContentLoaded: null,
  resourceLoadTimes: [],
  apiCallTimes: [],
  
  // Errors
  jsErrors: [],
  apiErrors: [],
};

// Performance observer callbacks
const observers = [];

// Track Core Web Vitals
export function useCoreWebVitals(onReport) {
  const reported = useRef(new Set());

  useEffect(() => {
    // Only run in browser
    if (typeof window === 'undefined') return;

    // First Contentful Paint (FCP)
    const fcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach(entry => {
        if (!reported.current.has('FCP')) {
          metrics.FCP = entry.startTime;
          reported.current.add('FCP');
          if (onReport) onReport('FCP', entry.startTime);
          
          // Log to GA4
          if (window.gtag) {
            window.gtag('event', 'FCP', {
              event_category: 'Core Web Vitals',
              event_label: entry.startTime.toString(),
              value: Math.round(entry.startTime),
              non_interaction: true,
            });
          }
        }
      });
    });

    // Largest Contentful Paint (LCP)
    const lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      if (!reported.current.has('LCP')) {
        metrics.LCP = lastEntry.startTime;
        reported.current.add('LCP');
        if (onReport) onReport('LCP', lastEntry.startTime);
        
        if (window.gtag) {
          window.gtag('event', 'LCP', {
            event_category: 'Core Web Vitals',
            event_label: lastEntry.startTime.toString(),
            value: Math.round(lastEntry.startTime),
            non_interaction: true,
          });
        }
      }
    });

    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach(entry => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
          metrics.CLS = clsValue;
          if (onReport) onReport('CLS', clsValue);
        }
      });
    });

    // First Input Delay (FID) - using Interaction to Next Paint (INP) as replacement
    const inpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach(entry => {
        if (!reported.current.has('INP')) {
          metrics.FID = entry.processingStart - entry.startTime;
          reported.current.add('INP');
          if (onReport) onReport('INP', metrics.FID);
          
          if (window.gtag) {
            window.gtag('event', 'INP', {
              event_category: 'Core Web Vitals',
              event_label: metrics.FID.toString(),
              value: Math.round(metrics.FID),
              non_interaction: true,
            });
          }
        }
      });
    });

    // Start observing
    try {
      fcpObserver.observe({ type: 'paint', buffered: true });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      clsObserver.observe({ type: 'layout-shift', buffered: true });
      inpObserver.observe({ type: 'event', buffered: true, durationThreshold: 16 });
    } catch (e) {
      console.warn('Performance Observer not supported:', e);
    }

    // Cleanup
    return () => {
      fcpObserver.disconnect();
      lcpObserver.disconnect();
      clsObserver.disconnect();
      inpObserver.disconnect();
    };
  }, [onReport]);
}

// Track page load performance
export function usePageLoadTracking() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const trackLoadTime = () => {
      const timing = performance.timing;
      
      metrics.pageLoadTime = timing.loadEventEnd - timing.navigationStart;
      metrics.domContentLoaded = timing.domContentLoadedEventEnd - timing.navigationStart;
      metrics.TTFB = timing.responseStart - timing.navigationStart;

      // Log to GA4
      if (window.gtag) {
        window.gtag('event', 'page_load', {
          event_category: 'Performance',
          event_label: 'page_load_time',
          value: Math.round(metrics.pageLoadTime),
          non_interaction: true,
        });

        window.gtag('event', 'dom_content_loaded', {
          event_category: 'Performance',
          event_label: 'dom_content_loaded',
          value: Math.round(metrics.domContentLoaded),
          non_interaction: true,
        });

        window.gtag('event', 'ttfb', {
          event_category: 'Performance',
          event_label: 'time_to_first_byte',
          value: Math.round(metrics.TTFB),
          non_interaction: true,
        });
      }

    };

    if (document.readyState === 'complete') {
      trackLoadTime();
    } else {
      window.addEventListener('load', trackLoadTime);
    }

    return () => {
      window.removeEventListener('load', trackLoadTime);
    };
  }, []);
}

// Track resource loading times
export function useResourceTracking() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        if (entry.transferSize > 0) { // Only track resources with actual data transfer
          metrics.resourceLoadTimes.push({
            name: entry.name,
            duration: Math.round(entry.duration),
            transferSize: entry.transferSize,
            decodedBodySize: entry.decodedBodySize,
            initiatorType: entry.initiatorType,
          });
        }
      });
    });

    try {
      observer.observe({ type: 'resource', buffered: true });
    } catch (e) {
      console.warn('Resource Observer not supported:', e);
    }

    return () => observer.disconnect();
  }, []);
}

// Track JavaScript errors
export function useErrorTracking() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleError = (event) => {
      const error = {
        message: event.message || event.error?.message || 'Unknown error',
        filename: event.filename || event.error?.fileName,
        lineno: event.lineno || event.error?.lineNumber,
        colno: event.colno || event.error?.columnNumber,
        timestamp: Date.now(),
        userAgent: navigator.userAgent,
      };

      metrics.jsErrors.push(error);

      // Log to GA4
      if (window.gtag) {
        window.gtag('event', 'exception', {
          event_category: 'Error',
          event_label: error.message,
          value: error.lineno,
        });
      }

    };

    const handleUnhandledRejection = (event) => {
      // Prevent default to stop console errors
      event.preventDefault();
      
      const error = {
        reason: event.reason?.message || 'Unknown rejection reason',
        promise: event.promise,
        timestamp: Date.now(),
      };

      metrics.jsErrors.push({ ...error, type: 'unhandled_rejection' });

      if (window.gtag) {
        window.gtag('event', 'exception', {
          event_category: 'Error',
          event_label: `Unhandled Rejection: ${error.reason}`,
        });
      }

    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
}

// Track API call performance
export function trackApiCall(url, startTime, success = true) {
  const duration = Date.now() - startTime;
  
  metrics.apiCallTimes.push({
    url,
    duration,
    success,
    timestamp: Date.now(),
  });

  // Log slow API calls
  if (duration > 3000) {
  }

  // Log to GA4
  if (window.gtag) {
    window.gtag('event', 'api_call', {
      event_category: 'Performance',
      event_label: url,
      value: Math.round(duration),
    });
  }
}

// Get performance report
export function getPerformanceReport() {
  const report = {
    coreWebVitals: {
      FCP: metrics.FCP ? Math.round(metrics.FCP) : null,
      LCP: metrics.LCP ? Math.round(metrics.LCP) : null,
      FID: metrics.FID ? Math.round(metrics.FID) : null,
      CLS: metrics.CLS ? Math.round(metrics.CLS * 1000) / 1000 : null,
      TTFB: metrics.TTFB ? Math.round(metrics.TTFB) : null,
    },
    pageLoad: {
      pageLoadTime: metrics.pageLoadTime ? Math.round(metrics.pageLoadTime) : null,
      domContentLoaded: metrics.domContentLoaded ? Math.round(metrics.domContentLoaded) : null,
    },
    resources: {
      totalResources: metrics.resourceLoadTimes.length,
      totalTransferSize: metrics.resourceLoadTimes.reduce((sum, r) => sum + r.transferSize, 0),
      slowestResources: metrics.resourceLoadTimes
        .sort((a, b) => b.duration - a.duration)
        .slice(0, 5),
    },
    errors: {
      totalErrors: metrics.jsErrors.length,
      recentErrors: metrics.jsErrors.slice(-5),
    },
    api: {
      totalCalls: metrics.apiCallTimes.length,
      avgDuration: metrics.apiCallTimes.length > 0
        ? Math.round(metrics.apiCallTimes.reduce((sum, c) => sum + c.duration, 0) / metrics.apiCallTimes.length)
        : null,
      slowCalls: metrics.apiCallTimes.filter(c => c.duration > 3000).length,
    },
  };

  // Calculate performance score (simplified)
  let score = 100;
  if (report.coreWebVitals.LCP > 2500) score -= 20;
  if (report.coreWebVitals.FID > 100) score -= 20;
  if (report.coreWebVitals.CLS > 0.1) score -= 20;
  if (report.errors.totalErrors > 0) score -= 10;
  if (report.pageLoad.pageLoadTime > 3000) score -= 10;

  report.performanceScore = Math.max(0, score);

  return report;
}

// Log performance report to console
export function logPerformanceReport() {
  const report = getPerformanceReport();
  
}

// Main performance monitoring hook
export default function usePerformanceMonitor(options = {}) {
  const {
    trackCoreWebVitals = true,
    trackPageLoad = true,
    trackResources = true,
    trackErrors = true,
    onMetricReport = null,
  } = options;

  useCoreWebVitals(onMetricReport);
  usePageLoadTracking();
  useResourceTracking();
  useErrorTracking();

  // Expose report function
  return {
    getReport: getPerformanceReport,
    logReport: logPerformanceReport,
    trackApiCall,
  };
}